import { useMemo, useState } from 'react';
import {
  ChartDownloadStreamingCommon,
  ChartDownloadMetricListSteam,
  ChartDownloadMetricListNewzoo,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import {
  getPCConsoleMetricsTableDataRankList,
  fetchDataBrainRanksCommon,
  fetchGameStreamingRanks,
} from '@intelligence/services/topcharts';
import {
  defaultMetrictableChartsDownloadApiParams,
  defaultMobileChartsDownloadApiParams,
  defaultStreamingChartsDownloadApiParams,
  defaultSteamChartsDownloadApiParams,
  defaultNewzooChartsDownloadApiParams,
} from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils/constant';
import { toDateTypeLy } from '@/pages/intelligence/utils/common';
import moment from 'moment';
import { formatDateStr } from '@gp/utils/common';

// 游戏详情统一可视化下载，卡片配置对象内容
export default function useChartExportTableHooks(props: any) {
  const { entityType = '', tableParams = {}, chartDownloadConfig } = props;
  // console.log('useChartExportTableHooks', props);

  const [rankNum, setRankNum] = useState('-');
  const [requestLoading, setRequestLoading] = useState(true);

  const isChartDownloadSteam = useMemo(() => {
    const metricObj =
      ChartDownloadMetricListSteam?.find((item: any) => item.metricKey == tableParams.metric) ||
      ({} as any);
    return !isEmpty(metricObj);
  }, [tableParams.metric, entityType]);

  const isChartDownloadNewzoo = useMemo(() => {
    const metrickey =
      Array.isArray(tableParams?.metric) && tableParams?.metric?.length > 0
        ? tableParams?.metric[0]
        : tableParams.metric;
    const metricObj =
      ChartDownloadMetricListNewzoo?.find((item: any) => item.metricKey == metrickey) ||
      ({} as any);
    return !isEmpty(metricObj);
  }, [tableParams.metric, entityType]);

  const isStreaming = useMemo(() => {
    const metricObj =
      ChartDownloadStreamingCommon?.find(
        (item: any) => item.metricKey == tableParams.check_metric,
      ) || ({} as any);
    return !isEmpty(metricObj);
  }, [tableParams.check_metric, entityType]);

  const newFetchParams = useMemo(() => {
    let newParams = {};
    const commonParams = {
      date_type: tableParams.date_type,
      start_date: formatDateStr(tableParams?.start_date),
      end_date: formatDateStr(tableParams?.end_date),
      regions: tableParams.regions,
    };

    if (isStreaming) {
      newParams = {
        ...defaultStreamingChartsDownloadApiParams,
        ...commonParams,
        order_metric: tableParams.check_metric,
        app_ids: typeof tableParams.unified_id === 'string' ? [tableParams.unified_id] : [''],
        period: tableParams.period,
        platform: tableParams.platform,
      };
    } else if (isChartDownloadSteam) {
      newParams = {
        ...defaultSteamChartsDownloadApiParams,
        ...commonParams,
        date_type: tableParams.period,
        entity_type: entityType,
        unified_ids: tableParams.unified_ids,
        order_metric: tableParams.metric,
      };
    } else if (isChartDownloadNewzoo) {
      newParams = {
        ...defaultNewzooChartsDownloadApiParams,
        ...commonParams,
        date_type: toDateTypeLy[tableParams.date_type],
        entity_type: entityType,
        unified_ids: tableParams.unified_ids,
      };
    } else if (entityType === 'mobile') {
      newParams = {
        ...defaultMobileChartsDownloadApiParams,
        ...commonParams,
        data_sources:
          typeof tableParams.source === 'string'
            ? [tableParams.source]
            : defaultMobileChartsDownloadApiParams.data_sources,
        order_source: tableParams.source,
        order_metric: tableParams.metric,
        game_code: tableParams.unified_ids,
      };
    } else {
      newParams = {
        ...defaultMetrictableChartsDownloadApiParams,
        ...commonParams,
        entity_type: entityType,
        unified_ids: tableParams.unified_ids,
        device: tableParams.device,
        // 兜底为 market：tableParams 缺失 region_type 时避免 undefined 覆盖下载参数默认值
        region_type: tableParams?.region_type || 'market',
        segment: tableParams.segment === '' ? 'All' : tableParams.segment,
        order_metric: tableParams.metricSegVal || '',
        order_source: tableParams.source,
      };
    }
    return newParams;
  }, [tableParams, entityType]);

  const getEntityString = (entityType: string) => {
    switch (entityType) {
      case 'pc':
        return ' - PC';
      case 'console':
        return ' - Console';
      case 'combine':
        return ' - PC+Console';
      default:
        return '';
    }
  };
  const cardConfig = useMemo(() => {
    return {
      ...chartDownloadConfig,
      entityTitle: !isStreaming ? getEntityString(entityType) : '',
    };
  }, [chartDownloadConfig]);

  const getFetchFun = () => {
    if (isStreaming) {
      return fetchGameStreamingRanks;
    }
    if (entityType === 'mobile') {
      return fetchDataBrainRanksCommon;
    }
    return getPCConsoleMetricsTableDataRankList;
  };

  // 打开可视化下载前，先调榜单rank排名接口
  const getTopchartsRank = async () => {
    setRequestLoading(true);
    try {
      const { data = {} } = await getFetchFun()?.(newFetchParams);
      const gameList =
        data?.rank_au_list || data?.rank_list || data?.GamesRank || data?.trend || [];
      if (gameList?.length > 0) {
        const gameObj = gameList[0];
        setRankNum(gameObj.rank);
      } else {
        setRankNum('-');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRequestLoading(false);
    }
  };

  return {
    rankNum,
    requestLoading,
    getTopchartsRank,
    cardConfig,
  };
}
